import { createAsyncThunk } from '@reduxjs/toolkit';
import { streamingDataSlice } from '@src/providers/AppStateProvider/streamingDataSlice';
import type { AppExtraArgument, AppState, AppThunkDispatch } from '@src/providers/AppStateProvider/types';
import { getErrorMessage } from '@src/providers/AppStateProvider/wsEndpointBuilder';
import type { Security } from '@talos/kyoko';

export interface WaitForNewSymbolThunkParams {
  symbol: string;
  onSymbolAvailable: (dispatch: AppThunkDispatch) => void | Promise<void>;
  timeoutMs?: number;
}

/**
 * Generic thunk to wait for a symbol to become available in the SECURITY stream
 * and execute a callback when it becomes available.
 *
 * Uses RTK Query's streamingDataSlice to manage the WebSocket subscription.
 * Each symbol gets its own subscription via unique cache keys.
 * The subscription is automatically cleaned up on success, timeout, error, or cancellation.
 *
 * @example
 * ```typescript
 * // Basic usage
 * dispatch(waitForNewSymbolThunk({
 *   symbol: 'BTCUSD',
 *   onSymbolAvailable: (dispatch) => {
 *     dispatch(openView({ view: OMSView.ManualTradeForm }));
 *   },
 * }));
 *
 * // With custom timeout
 * dispatch(waitForNewSymbolThunk({
 *   symbol: 'ETHUSD',
 *   onSymbolAvailable: async (dispatch) => {
 *     await dispatch(someOtherAction());
 *     dispatch(openView({ view: OMSView.ManualDealTradeForm }));
 *   },
 *   timeoutMs: 5000,
 * }));
 * ```
 */
export const waitForNewSymbolThunk = createAsyncThunk<
  Security,
  WaitForNewSymbolThunkParams,
  { state: AppState; extra: AppExtraArgument }
>('oms/waitForNewSymbolThunk', async (params, thunkAPI) => {
  const { symbol, onSymbolAvailable, timeoutMs = 10000 } = params;
  const { dispatch, signal, getState } = thunkAPI;

  if (signal?.aborted) {
    throw new Error('Thunk was cancelled before subscription started');
  }

  const queryArgs = {
    Symbols: [symbol],
    tag: 'waitForNewSymbolThunk',
  };

  const selectCacheEntry = streamingDataSlice.endpoints.getSecurities.select(queryArgs);
  const queryPromise = dispatch(streamingDataSlice.endpoints.getSecurities.initiate(queryArgs));

  let pollInterval: ReturnType<typeof setInterval> | undefined;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const cleanup = () => {
    pollInterval && clearInterval(pollInterval);
    timeoutId && clearTimeout(timeoutId);
    signal?.removeEventListener('abort', cleanup);
    queryPromise.unsubscribe();
  };

  signal?.addEventListener('abort', cleanup);

  try {
    // First, check if symbol is already in cache
    const initialCacheEntry = selectCacheEntry(getState());
    const initialSecurity = initialCacheEntry?.data?.get(symbol);
    if (initialSecurity) {
      cleanup();
      await onSymbolAvailable(dispatch);
      return initialSecurity;
    }

    return new Promise<Security>((resolve, reject) => {
      timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error(`Timeout: Symbol ${symbol} did not become available within ${timeoutMs}ms`));
      }, timeoutMs);

      let previousCacheEntry = selectCacheEntry(getState());

      const checkCache = () => {
        if (signal?.aborted) {
          cleanup();
          reject(new Error('Thunk was cancelled'));
          return;
        }

        const cacheEntry = selectCacheEntry(getState());

        // Only process when cache reference actually changes (optimization)
        if (cacheEntry !== previousCacheEntry) {
          previousCacheEntry = cacheEntry;

          if (cacheEntry?.error) {
            cleanup();
            reject(new Error(`WebSocket error: ${getErrorMessage(cacheEntry.error)}`));
            return;
          }

          const security = cacheEntry?.data?.get(symbol);
          if (security) {
            cleanup();
            resolve(security);
            return;
          }
        }
      };

      checkCache();
      pollInterval = setInterval(checkCache, 100);
    }).then(async security => {
      await onSymbolAvailable(dispatch);
      return security;
    });
  } catch (err) {
    cleanup();
    throw err;
  }
});
